/* TOMB CLOCK — shared storefront behaviour: dialogs, predictive search, real Shopify cart,
   quick add / choose options, carousels, announcement rotation, localization, newsletter. */

let Ev = null;
try {
  Ev = await import('@shopify/events');
} catch (error) {
  Ev = null;
}

const TC = (window.TC = window.TC || {});
const S = TC.strings || {};
const LINES_UPDATE = Ev?.StandardEvents?.cartLinesUpdate || 'shopify:cart:lines-update';
const routes = TC.routes || {};
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const t = (key, vars = {}) => {
  let s = S[key] || key;
  for (const [k, v] of Object.entries(vars)) s = s.split(`[${k}]`).join(v);
  return s;
};

/* ---------- Money ---------- */
function formatMoney(cents, format = TC.moneyFormat || '${{amount}}') {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = Number(cents) || 0;
  const re = /\{\{\s*(\w+)\s*\}\}/;
  const fmt = (n, dec = 2, th = ',', dp = '.') => {
    const fixed = (n / 100).toFixed(dec);
    const [int, frac] = fixed.split('.');
    return int.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${th}`) + (frac ? dp + frac : '');
  };
  const m = format.match(re);
  if (!m) return format;
  let out;
  switch (m[1]) {
    case 'amount_no_decimals': out = fmt(value, 0); break;
    case 'amount_with_comma_separator': out = fmt(value, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': out = fmt(value, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': out = fmt(value, 2, "'", '.'); break;
    case 'amount_no_decimals_with_space_separator': out = fmt(value, 0, ' ', ''); break;
    case 'amount_with_space_separator': out = fmt(value, 2, ' ', ','); break;
    case 'amount_with_period_and_space_separator': out = fmt(value, 2, ' ', '.'); break;
    default: out = fmt(value, 2);
  }
  return format.replace(re, out);
}
TC.formatMoney = formatMoney;

/* ---------- Toast ---------- */
let toastTimer;
function toast(message, tone = 'info') {
  const el = document.querySelector('[data-tc-toast]');
  if (!el || !message) return;
  el.textContent = message;
  el.dataset.tone = tone;
  el.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (el.hidden = true), 2800);
}
TC.toast = toast;

/* ---------- Cart ---------- */
const DRAWER_SECTION = 'tc-cart-drawer';
const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

async function fetchCart() {
  const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
  if (!res.ok) throw new Error(`Cart ${res.status}`);
  return res.json();
}

function sectionInner(html) {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const wrap = doc.querySelector('.shopify-section') || doc.body;
  return wrap.innerHTML;
}

function renderDrawer(html) {
  const host = document.querySelector('[data-tc-cart-content]');
  if (!host || html == null) return;
  const active = document.activeElement;
  const activeKey = active?.closest?.('[data-tc-line]')?.dataset.key;
  const activeSel = active?.matches?.('[data-tc-qty-input]') ? '[data-tc-qty-input]' : active?.dataset?.tcQty ? `[data-tc-qty="${active.dataset.tcQty}"]` : null;
  host.innerHTML = html;
  const state = host.querySelector('[data-tc-cart-state]');
  if (state) {
    const title = document.querySelector('[data-tc-cart-title]');
    if (title) title.textContent = state.dataset.title;
    updateCount(Number(state.dataset.count));
  }
  if (activeKey && activeSel) {
    const line = host.querySelector(`[data-tc-line][data-key="${CSS.escape(activeKey)}"] ${activeSel}`);
    line?.focus();
  }
}

async function refreshDrawer() {
  try {
    const url = `${routes.root_url.replace(/\/$/, '')}/?sections=${DRAWER_SECTION}`;
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    const data = await res.json();
    renderDrawer(sectionInner(data[DRAWER_SECTION]));
  } catch (error) {
    try {
      const cart = await fetchCart();
      updateCount(cart.item_count);
    } catch (_) { /* offline */ }
  }
}
TC.refreshCart = refreshDrawer;

function updateCount(count) {
  if (!Number.isFinite(count)) return;
  document.querySelectorAll('[data-tc-cart-count]').forEach((el) => {
    el.textContent = count > 99 ? '99+' : String(count);
    el.hidden = count === 0;
  });
  document.querySelectorAll('[data-tc-cart-button]').forEach((btn) => {
    const label = count > 0 ? btn.dataset.labelCount?.replace('[count]', count) : btn.dataset.labelEmpty;
    if (label) btn.setAttribute('aria-label', label);
  });
  try {
    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
  } catch (_) { /* storage blocked */ }
}

function showDrawerError(message) {
  const el = document.querySelector('[data-tc-cart-error]');
  if (el) {
    el.textContent = message;
    el.hidden = !message;
  }
}

function dispatchLines(target, action, lines) {
  if (!Ev?.CartLinesUpdateEvent?.createPromise) return null;
  try {
    const deferred = Ev.CartLinesUpdateEvent.createPromise();
    (target || document).dispatchEvent(
      new Ev.CartLinesUpdateEvent({ action, context: action === 'add' ? 'product' : 'cart', lines, promise: deferred.promise })
    );
    return deferred;
  } catch (_) {
    return null;
  }
}

function resolveLines(deferred, cart, extra = {}) {
  if (!deferred) return;
  try {
    deferred.resolve({
      cart: Ev.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'tc', didError: false, ...extra },
    });
  } catch (_) { /* consumer error */ }
}

function errorMessage(json, fallback) {
  return (json && (json.description || json.message)) || fallback || S.cart_error || 'Error';
}

async function cartAdd(items, { target } = {}) {
  const deferred = dispatchLines(target, 'add', items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 })));
  let res;
  let json;
  try {
    res = await fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({ items, sections: DRAWER_SECTION, sections_url: window.location.pathname }),
    });
    json = await res.json();
  } catch (error) {
    deferred?.reject(error);
    Ev?.CartErrorEvent && document.dispatchEvent(new Ev.CartErrorEvent({ error: 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    throw new Error(S.network_error || 'Network error');
  }
  if (!res.ok || json.status) {
    const msg = errorMessage(json);
    try {
      const cart = await fetchCart();
      resolveLines(deferred, cart, { didError: true });
    } catch (_) {
      deferred?.reject(new Error(msg));
    }
    throw new Error(msg);
  }
  renderDrawer(sectionInner(json.sections?.[DRAWER_SECTION]));
  const cart = await fetchCart().catch(() => null);
  if (cart) {
    updateCount(cart.item_count);
    resolveLines(deferred, cart, { sections: json.sections });
  }
  showDrawerError('');
  return json;
}

async function cartChange(key, quantity, { target } = {}) {
  const deferred = dispatchLines(target, quantity === 0 ? 'remove' : 'update', [{ id: key, quantity }]);
  let res;
  let json;
  try {
    res = await fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity, sections: DRAWER_SECTION, sections_url: window.location.pathname }),
    });
    json = await res.json();
  } catch (error) {
    deferred?.reject(error);
    throw new Error(S.network_error || 'Network error');
  }
  if (!res.ok || json.status) {
    const msg = errorMessage(json);
    await refreshDrawer();
    try {
      resolveLines(deferred, await fetchCart(), { didError: true });
    } catch (_) {
      deferred?.reject(new Error(msg));
    }
    throw new Error(msg);
  }
  renderDrawer(sectionInner(json.sections?.[DRAWER_SECTION]));
  updateCount(json.item_count);
  resolveLines(deferred, json, { sections: json.sections });
  return json;
}

TC.cart = { add: cartAdd, change: cartChange, refresh: refreshDrawer };

function afterAdd(title) {
  const layers = document.querySelector('tc-header-layers');
  if (layers && TC.config?.autoOpenCart !== false && TC.template !== 'cart') {
    layers.open('cart');
  } else if (!layers) {
    window.location.href = routes.cart_url;
    return;
  }
  toast(title ? t('added_named', { title }) : S.added || 'Added to cart');
}
TC.afterAdd = afterAdd;
TC.openCart = () => document.querySelector('tc-header-layers')?.open('cart');

/* Cart events from other components (Horizon cart page, apps, Shopify.actions). */
document.addEventListener(LINES_UPDATE, (event) => {
  const promise = event.promise;
  if (!promise) return;
  promise
    .then((result) => {
      const detail = result?.detail || {};
      if (detail.source === 'tc') return;
      refreshDrawer().then(() => {
        if (event.action === 'add' && !detail.didError && TC.config?.autoOpenCart !== false && !event.target?.closest?.('dialog:modal')) {
          document.querySelector('tc-header-layers')?.open('cart');
        }
      });
    })
    .catch(() => {});
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshDrawer();
});

/* Quick add forms (product cards). */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('form[data-tc-quick-add]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  if (button?.getAttribute('aria-busy') === 'true') return;
  const fd = new FormData(form);
  const item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 };
  if (fd.get('selling_plan')) item.selling_plan = Number(fd.get('selling_plan'));
  const label = button?.querySelector('[data-tc-label]');
  button?.setAttribute('aria-busy', 'true');
  if (button) button.disabled = true;
  if (label) label.textContent = S.adding || 'Adding…';
  try {
    await cartAdd([item], { target: form });
    const title = form.closest('.tc-card')?.querySelector('.tc-card__title')?.textContent?.trim();
    afterAdd(title);
  } catch (error) {
    toast(error.message, 'error');
  } finally {
    button?.removeAttribute('aria-busy');
    if (button) button.disabled = false;
    if (label) label.textContent = button?.dataset.label || S.add_to_cart;
  }
});

/* Choose options buttons. */
document.addEventListener('click', (event) => {
  const btn = event.target.closest?.('[data-tc-options]');
  if (!btn) return;
  event.preventDefault();
  const layers = document.querySelector('tc-header-layers');
  if (layers) layers.openOptions(btn.dataset.tcOptions, btn);
  else window.location.href = btn.dataset.tcOptions;
});

/* Localization selectors submit on change. */
document.addEventListener('change', (event) => {
  const select = event.target.closest?.('form[data-tc-locale] select');
  if (select) select.form.submit();
});

/* Newsletter: client-side validation and focus on server result. */
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!form.matches?.('form.tc-news')) return;
  const input = form.querySelector('[data-tc-email]');
  const msg = form.querySelector('[data-tc-news-invalid]');
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((input?.value || '').trim());
  if (!ok) {
    event.preventDefault();
    input?.setAttribute('aria-invalid', 'true');
    if (msg) {
      msg.hidden = false;
      msg.id = msg.id || `${form.id}-invalid`;
      input?.setAttribute('aria-describedby', msg.id);
    }
    input?.focus();
  } else if (msg) {
    msg.hidden = true;
  }
}, true);
document.querySelectorAll('[data-tc-news-result]').forEach((el) => {
  el.scrollIntoView({ block: 'center' });
  el.focus({ preventScroll: true });
});

/* ---------- Header layers: menu, search, cart drawer, options dialog ---------- */
const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class TcHeaderLayers extends HTMLElement {
  connectedCallback() {
    this.backdrop = this.querySelector('[data-tc-backdrop]');
    this.active = null;
    this.trigger = null;
    this.searchCtrl = null;
    this.searchTimer = null;
    const cfgEl = this.parentElement?.querySelector('[data-tc-header-config]') || document.querySelector('[data-tc-header-config]');
    try {
      this.config = JSON.parse(cfgEl?.textContent || '{}');
    } catch (_) {
      this.config = {};
    }
    TC.config = { ...(TC.config || {}), ...this.config };

    this.onDocClick = this.onDocClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onChange = this.onChange.bind(this);
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKey);
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);

    const header = this.parentElement?.querySelector('.tc-header');
    if (header && 'ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => document.documentElement.style.setProperty('--tc-header-h', `${header.offsetHeight}px`));
      this.ro.observe(header);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKey);
    this.ro?.disconnect();
    if (this.active) this.unlock();
  }

  panel(name) {
    return this.querySelector(`[data-tc-panel="${name}"]`);
  }

  lock() {
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.overflow = 'hidden';
  }

  unlock() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.overflow = '';
  }

  setExpanded(name, value) {
    document.querySelectorAll(`[data-tc-open="${name}"]`).forEach((el) => el.setAttribute('aria-expanded', String(value)));
  }

  open(name, trigger) {
    const panel = this.panel(name);
    if (!panel) return false;
    if (this.active === panel) return true;
    const previousTrigger = this.trigger;
    if (this.active) this.close({ restore: false });
    this.trigger = trigger || previousTrigger || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    panel.hidden = false;
    this.backdrop.hidden = false;
    this.active = panel;
    this.lock();
    this.setExpanded(name, true);
    requestAnimationFrame(() => {
      const target = name === 'search' ? panel.querySelector('[data-tc-search-input]') : panel.querySelector('.tc-close') || panel;
      target?.focus({ preventScroll: true });
      if (name === 'search') {
        const input = panel.querySelector('[data-tc-search-input]');
        if (input?.value) input.select();
      }
    });
    return true;
  }

  close({ restore = true } = {}) {
    const panel = this.active;
    if (!panel) return;
    panel.hidden = true;
    this.backdrop.hidden = true;
    this.setExpanded(panel.dataset.tcPanel, false);
    this.active = null;
    this.unlock();
    if (panel.dataset.tcPanel === 'search') this.searchCtrl?.abort();
    if (restore && this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
    if (restore) this.trigger = null;
  }

  onDocClick(event) {
    const opener = event.target.closest('[data-tc-open]');
    if (opener && !this.contains(opener)) {
      const name = opener.dataset.tcOpen;
      if (this.panel(name)) {
        event.preventDefault();
        this.open(name, opener);
      }
      return;
    }
    if (event.target === this.backdrop) this.close();
  }

  onKey(event) {
    if (!this.active) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...this.active.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) {
      event.preventDefault();
      this.active.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (!this.active.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onClick(event) {
    const target = event.target;
    if (target.closest('[data-tc-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    const term = target.closest('[data-tc-search-term]');
    if (term) {
      event.preventDefault();
      const input = this.querySelector('[data-tc-search-input]');
      if (input) {
        input.value = term.dataset.tcSearchTerm;
        input.focus();
        this.runSearch(input.value, true);
      }
      return;
    }
    const qtyBtn = target.closest('[data-tc-qty]');
    if (qtyBtn) {
      const line = qtyBtn.closest('[data-tc-line]');
      const input = line?.querySelector('[data-tc-qty-input]');
      if (!input) return;
      const step = Number(input.step) || 1;
      const current = Number(input.value) || 0;
      const min = Number(input.dataset.min) || 1;
      let next = qtyBtn.dataset.tcQty === 'plus' ? current + step : current - step;
      if (next > 0 && next < min) next = 0;
      if (input.max && next > Number(input.max)) next = Number(input.max);
      this.updateLine(line, Math.max(0, next));
      return;
    }
    const remove = target.closest('[data-tc-remove]');
    if (remove) {
      this.updateLine(remove.closest('[data-tc-line]'), 0);
      return;
    }
    const opt = target.closest('[data-tc-opt]');
    if (opt) {
      this.selectOption(opt);
      return;
    }
    const addSel = target.closest('[data-tc-opts-add]');
    if (addSel) this.addSelected(addSel);
  }

  onChange(event) {
    const input = event.target.closest('[data-tc-qty-input]');
    if (!input) return;
    const value = Math.max(0, parseInt(input.value, 10) || 0);
    this.updateLine(input.closest('[data-tc-line]'), value);
  }

  async updateLine(line, quantity) {
    if (!line || line.getAttribute('aria-busy') === 'true') return;
    line.setAttribute('aria-busy', 'true');
    try {
      await cartChange(line.dataset.key, quantity, { target: this });
      if (quantity === 0) toast(S.removed || 'Removed');
    } catch (error) {
      showDrawerError(error.message);
    } finally {
      line.removeAttribute('aria-busy');
    }
  }

  /* Search */
  onInput(event) {
    const input = event.target.closest('[data-tc-search-input]');
    if (!input) return;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.runSearch(input.value), this.config.searchDelay || 260);
  }

  async runSearch(raw, immediate = false) {
    const q = raw.trim();
    const panel = this.panel('search');
    const idle = panel.querySelector('[data-tc-search-idle]');
    const status = panel.querySelector('[data-tc-search-status]');
    const results = panel.querySelector('[data-tc-search-results]');
    const input = panel.querySelector('[data-tc-search-input]');
    this.searchCtrl?.abort();
    if (!q) {
      idle.hidden = false;
      status.hidden = true;
      results.innerHTML = '';
      return;
    }
    idle.hidden = true;
    status.classList.remove('tc-vh');
    status.hidden = false;
    status.textContent = S.searching || 'Searching…';
    results.innerHTML = '';
    const ctrl = new AbortController();
    this.searchCtrl = ctrl;
    const params = new URLSearchParams({
      q,
      section_id: 'tc-predictive-search',
      'resources[type]': 'product,collection',
      'resources[limit]': input?.dataset.limit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });
    try {
      const res = await fetch(`${this.config.searchUrl || routes.predictive_search_url}?${params}`, { signal: ctrl.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const html = await res.text();
      if (ctrl.signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-tc-predictive]');
      if (!content) throw new Error('empty');
      results.innerHTML = content.outerHTML;
      const count = Number(content.dataset.count || 0);
      if (count > 0) {
        status.textContent = content.querySelector('[data-tc-count-text]')?.textContent?.trim() || '';
        status.classList.add('tc-vh');
      } else {
        status.hidden = true;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      status.hidden = false;
      status.textContent = S.search_error || 'Search is unavailable. Press Enter to see all results.';
    }
  }

  /* Options dialog */
  async openOptions(url, trigger) {
    const panel = this.panel('options');
    const body = panel.querySelector('[data-tc-opts-body]');
    body.innerHTML = `<p class="tc-opts__loading" role="status">${S.loading || 'Loading…'}</p>`;
    this.open('options', trigger);
    try {
      const res = await fetch(`${url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const product = await res.json();
      this.optsProduct = product;
      this.optsUrl = url;
      const first = product.variants.find((v) => v.available) || product.variants[0];
      this.optsSel = [...(first?.options || [])];
      this.renderOptions();
    } catch (error) {
      body.innerHTML = `<p class="tc-opts__loading" role="alert">${S.options_error || 'Could not load options.'}</p><a class="tc-opts__link" href="${url}">${S.view_product || 'View product'}</a>`;
    }
  }

  currentVariant() {
    const p = this.optsProduct;
    return p?.variants.find((v) => v.options.every((o, i) => o === this.optsSel[i])) || null;
  }

  renderOptions() {
    const p = this.optsProduct;
    const panel = this.panel('options');
    const body = panel.querySelector('[data-tc-opts-body]');
    const v = this.currentVariant();
    const img = v?.featured_image?.src || p.featured_image;
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const price = v ? formatMoney(v.price) : formatMoney(p.price);
    const compare = v && v.compare_at_price > v.price ? `<s><span class="tc-vh">${esc(S.regular_price || '')}</span>${formatMoney(v.compare_at_price)}</s>` : '';
    const options = (p.options || []).map((opt, i) => {
      const name = typeof opt === 'string' ? opt : opt.name;
      const values = typeof opt === 'string' ? [...new Set(p.variants.map((x) => x.options[i]))] : opt.values;
      if (values.length === 1 && p.options.length === 1 && name === 'Title') return '';
      const buttons = values.map((value) => {
        const test = [...this.optsSel];
        test[i] = value;
        const match = p.variants.find((x) => x.options.every((o, j) => o === test[j]));
        const unavailable = !match || !match.available;
        const pressed = this.optsSel[i] === value;
        return `<button type="button" class="tc-opt" data-tc-opt data-index="${i}" data-value="${esc(value)}" aria-pressed="${pressed}" data-unavailable="${unavailable}">${esc(value)}${unavailable ? `<span class="tc-vh"> ${esc(S.unavailable || '')}</span>` : ''}</button>`;
      }).join('');
      return `<fieldset><legend>${esc(name)}</legend><div class="tc-opts__values">${buttons}</div></fieldset>`;
    }).join('');
    let cta = S.add_to_cart || 'Add to cart';
    let disabled = false;
    if (p.requires_selling_plan) { cta = S.view_product || 'View product'; disabled = true; }
    else if (!v) { cta = S.unavailable || 'Unavailable'; disabled = true; }
    else if (!v.available) { cta = S.sold_out || 'Sold out'; disabled = true; }
    body.innerHTML = `
      <div class="tc-opts__head">
        <div class="tc-opts__media">${img ? `<img src="${esc(img)}${img.includes('?') ? '&' : '?'}width=160" alt="" width="72" height="72">` : ''}</div>
        <div class="tc-opts__info">
          <h2 class="tc-opts__title" id="TcOptsTitle">${esc(p.title)}</h2>
          <p class="tc-opts__price">${price} ${compare}</p>
        </div>
        <button type="button" class="tc-close" data-tc-close aria-label="${esc(S.close || 'Close')}">✕</button>
      </div>
      ${options}
      <p class="tc-opts__msg" role="status" aria-live="polite" data-tc-opts-msg></p>
      <button type="button" class="tc-btn tc-btn--light" data-tc-opts-add ${disabled ? 'disabled' : ''} style="min-height:52px">${esc(cta)}</button>
      <a class="tc-opts__link" href="${esc(this.optsUrl)}${v ? `?variant=${v.id}` : ''}">${esc(S.view_details || 'View full details')}</a>`;
  }

  selectOption(btn) {
    const i = Number(btn.dataset.index);
    this.optsSel[i] = btn.dataset.value;
    const p = this.optsProduct;
    if (!this.currentVariant()) {
      const fallback = p.variants.find((v) => v.options[i] === btn.dataset.value && v.available) || p.variants.find((v) => v.options[i] === btn.dataset.value);
      if (fallback) this.optsSel = [...fallback.options];
    }
    this.renderOptions();
    this.panel('options').querySelector(`[data-tc-opt][data-index="${i}"][aria-pressed="true"]`)?.focus();
  }

  async addSelected(button) {
    const v = this.currentVariant();
    if (!v || !v.available || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.textContent = S.adding || 'Adding…';
    try {
      await cartAdd([{ id: v.id, quantity: v.quantity_rule?.min || 1 }], { target: this });
      const title = this.optsProduct.title;
      this.close({ restore: false });
      afterAdd(v.title && v.title !== 'Default Title' ? `${title} (${v.title})` : title);
    } catch (error) {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.textContent = S.add_to_cart || 'Add to cart';
      const msg = this.querySelector('[data-tc-opts-msg]');
      if (msg) msg.textContent = error.message;
    }
  }
}
if (!customElements.get('tc-header-layers')) customElements.define('tc-header-layers', TcHeaderLayers);

/* ---------- Carousels ---------- */
class TcCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-tc-track]');
    this.prev = this.querySelector('[data-tc-prev]');
    this.next = this.querySelector('[data-tc-next]');
    if (!this.track) return;
    this.update = this.update.bind(this);
    this.onPrev = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.ro = new ResizeObserver(this.update);
    this.ro.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.update);
    this.ro?.disconnect();
  }

  go(dir) {
    const card = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const step = card ? card.getBoundingClientRect().width + gap : 240;
    const n = Math.max(1, Math.floor((this.track.clientWidth - gap) / step));
    this.track.scrollBy({ left: dir * step * n * (document.dir === 'rtl' ? -1 : 1), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflow = scrollWidth - clientWidth > 4;
    const arrows = this.querySelector('[data-tc-arrows]');
    if (arrows) arrows.hidden = !overflow;
    if (this.prev) this.prev.disabled = Math.abs(scrollLeft) < 4;
    if (this.next) this.next.disabled = Math.abs(scrollLeft) + clientWidth >= scrollWidth - 4;
  }

  scrollToChild(el) {
    if (!el || !this.track) return;
    this.track.scrollTo({ left: el.offsetLeft - this.track.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft), behavior: 'auto' });
  }
}
if (!customElements.get('tc-carousel')) customElements.define('tc-carousel', TcCarousel);

/* ---------- Announcement ---------- */
class TcAnnouncement extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.tc-announce__item')];
    this.index = 0;
    const key = `tc-announce-dismissed-${this.dataset.section}`;
    this.key = key;
    if (this.hasAttribute('data-dismissible') && !window.Shopify?.designMode) {
      try {
        if (sessionStorage.getItem(key)) {
          this.hidden = true;
          return;
        }
      } catch (_) { /* storage blocked */ }
    }
    this.onDismiss = (e) => {
      if (!e.target.closest('[data-tc-dismiss]')) return;
      this.hidden = true;
      try {
        sessionStorage.setItem(key, '1');
      } catch (_) { /* storage blocked */ }
    };
    this.addEventListener('click', this.onDismiss);
    this.pause = () => (this.paused = true);
    this.resume = () => (this.paused = false);
    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);
    if (this.items.length > 1 && !reduceMotion.matches) {
      this.timer = setInterval(() => !this.paused && this.show(this.index + 1), (Number(this.dataset.interval) || 5) * 1000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  show(i) {
    if (!this.items.length) return;
    this.index = (i + this.items.length) % this.items.length;
    this.items.forEach((el, n) => {
      if (n === this.index) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', 'true');
    });
  }
}
if (!customElements.get('tc-announcement')) customElements.define('tc-announcement', TcAnnouncement);

/* ---------- Theme editor ---------- */
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const ann = block.closest?.('tc-announcement');
    if (ann) {
      ann.paused = true;
      ann.show(Number(block.dataset.index));
    }
    const carousel = block.closest?.('tc-carousel');
    if (carousel) carousel.scrollToChild(block.closest('[data-tc-track] > *'));
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const ann = event.target.closest?.('tc-announcement');
    if (ann) ann.paused = false;
  });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector?.('tc-header-layers')) {
      document.documentElement.removeAttribute('scroll-lock');
      document.body.style.overflow = '';
    }
  });
}
